import { Controller } from "@hotwired/stimulus";
import { post } from "@rails/request.js";
import debounce from "lodash/debounce";
import { createRinsedEvent, trackPurchase } from "../tracking";
import { setupRecaptcha } from "helpers/recaptcha";
import { logger } from "../datadog";
import { sanitizeCreditCard } from "../sanitizers";

export default class extends Controller {
  static outlets = ["payment-update"];
  static targets = [
    "anonymousId",
    "checkoutError",
    "checkoutFormId",
    "checkoutSuccess",
    "discount",
    "discountCode",
    "discountCodeStatus",
    "discountRow",
    "email",
    "firstName",
    "googleClientId",
    "googleSessionId",
    "lastName",
    "licensePlate",
    "locationSelect",
    "parentUrl",
    "phone",
    "product",
    "productPrice",
    "quantity",
    "recaptchaDiv",
    "recaptchaInput",
    "slowMessage",
    "subtotal",
    "tax",
    "taxEstimate",
    "tosContent",
    "total",
    "totalWithTax",
  ];

  static values = {
    nexusQuoteUrl: String,
    phoneOrEmailRequired: Boolean,
    products: Array,
    recaptchaEnabled: Boolean,
    recaptchaSiteKey: String,
    showTotalCost: Boolean,
    taxEstimateRequiresPii: Boolean,
    taxForSalesEnabled: Boolean,
    tosContentPath: String,
  };

  initialize() {
    // Bumped per tax estimate so only the newest response updates the DOM. Set here rather than in
    // connect(), which fires an estimate on its first line.
    this.taxEstimateRequestId = 0;
    // The Nexus quote's own counter — see quoteFromNexus for why it can't share the one above.
    this.nexusQuoteToken = 0;
    // The code the current prices were quoted with, which is only ever set by Apply. Typing must not
    // move it: the prices on screen still belong to the last applied code until the shopper commits.
    this.appliedDiscountCode = "";
    // The code the server confirmed it actually quoted with, which is not always the one applied.
    // null means no answer yet — distinct from "", which is the server declining to quote a code.
    this.nexusAcceptedCode = null;
  }

  connect() {
    this.estimateTax();
    if (
      this.productsValue.length === 1 &&
      !this.taxForSalesEnabledValue &&
      this.showTotalCostValue
    ) {
      this.updateTotal();
    }

    this.runDebouncedEstimateTax = debounce(this.estimateTax.bind(this), 1500);
    this.debouncedTrackEmailEntered = debounce(this.trackEmailEntered.bind(this), 500);
    this.setAnonymousId();
    this.trackPrefilledEmail();
    this.paymentUpdateOutlet.onFormSubmit(this.startSlowMessageCountdown.bind(this));
    this.invalidateQuantityInputs();
    if (this.phoneOrEmailRequiredValue) {
      this.setupPhoneOrEmailValidation();
    }
    if (this.recaptchaEnabledValue) {
      console.log("ReCAPTCHA is enabled - connecting...");
      this.setupRecaptcha();
    }
    if (this.hasDiscountCodeTarget) {
      this.paymentUpdateOutlet.addAsyncSubmitInterrupt(async () => this.validateDiscountCode());
    }
    this.boundHandleMessage = this.handleMessage.bind(this);
    window.addEventListener("message", this.boundHandleMessage);

    this.errorListeners = [];
    this.setGoogleAnalyticsValues();
  }

  disconnect() {
    window.removeEventListener("message", this.boundHandleMessage);
    this.errorListeners = [];
  }

  addErrorListener(listener) {
    this.errorListeners.push(listener);
  }

  setupRecaptcha() {
    console.log("Calling setupRecaptcha in checkout_forms Stimulus controller");

    const executeRecaptcha = setupRecaptcha(this.recaptchaDivTarget, this.recaptchaSiteKeyValue);
    this.paymentUpdateOutlet.addAsyncSubmitInterrupt(async () => {
      console.log("Adding ReCAPTCHA submit interrupt...");
      this.recaptchaInputTarget.value = await executeRecaptcha();
      return true;
    });
  }

  invalidateQuantityInputs() {
    if (this.hasQuantityTarget) {
      this.quantityTargets[0].setCustomValidity("At least one product is required.");
    }
  }

  setGoogleAnalyticsValues() {
    if (typeof gtag !== "undefined") {
      // the "target" param doesn't seem to require an actual Google measurement ID
      const target = "G-XXXXXXXXXX";
      gtag("get", target, "client_id", (val) => {
        this.googleClientIdTarget.value = val;
      });
      gtag("get", target, "session_id", (val) => {
        this.googleSessionIdTarget.value = val;
      });
    }
  }

  checkoutSuccessTargetConnected(target) {
    const parent = target.dataset.parent;
    const url = target.dataset.url;
    const {
      purchaseOrderId,
      purchaseOrderEid,
      salesOrderTax,
      salesOrderShipping,
      salesOrderValue,
      purchaseSource,
      purchaseItems,
      salespathCustomer,
      salespathProductName,
      salespathVoucher,
      salespathSaleId,
      salespathLoyaltyEnroll,
      serverSideGoogleAnalytics,
    } = target.dataset;

    const items = purchaseItems ? JSON.parse(purchaseItems) : [];

    if (salespathCustomer) {
      window.parent.postMessage(
        {
          message: "checkoutSuccess",
          customerName: salespathCustomer,
          productName: salespathProductName,
          voucherBarcode: salespathVoucher,
          posSaleId: salespathSaleId,
          loyaltyEnrollment: salespathLoyaltyEnroll === "true",
        },
        "https://app.ignitewash.com"
      );
    }

    const callback = () => {
      if (purchaseSource === "loyalty_program_reward_redemption") {
        window.location.href = `/portal/loyalty_programs/checkout_forms/success/${purchaseOrderId}`;
      } else if (parent && parent !== "false") {
        window.top.location.href = url;
      } else {
        window.location.href = url;
      }
    };

    try {
      if (typeof gtag === "undefined") {
        this.log("google gtag undefined", { rinsed: { purchaseOrderEid } });
      } else {
        this.log("google gtag defined", { rinsed: { purchaseOrderEid } });
        gtag("get", "G-XXXXXXXXXX", "client_id", (clientId) => {
          this.log("google gtag client_id", { rinsed: { purchaseOrderEid, clientId } });
        });
        gtag("get", "G-XXXXXXXXXX", "session_id", (sessionId) => {
          this.log("google gtag session_id", { rinsed: { purchaseOrderEid, sessionId } });
        });
      }
    } catch (error) {
      this.log("google gtag log error", { error: error.stack });
    }

    console.log("[Rinsed] checkout_forms_controller: calling trackPurchase", {
      transaction_id: purchaseOrderId,
      transaction_eid: purchaseOrderEid,
      value: salesOrderValue,
      tax: salesOrderTax,
      shipping: salesOrderShipping,
      currency: "USD",
      purchase_source: purchaseSource,
      items,
      serverSideGoogleAnalytics,
    });

    trackPurchase(
      {
        transaction_id: purchaseOrderId,
        transaction_eid: purchaseOrderEid,
        value: salesOrderValue,
        tax: salesOrderTax,
        shipping: salesOrderShipping,
        currency: "USD",
        purchase_source: purchaseSource,
        items,
      },
      serverSideGoogleAnalytics,
      callback
    );
  }

  checkoutErrorTargetConnected(target) {
    const { salespathCustomer, salespathError } = target.dataset;

    if (salespathCustomer) {
      window.parent.postMessage(
        {
          message: "checkoutError",
          customerName: salespathCustomer,
          errorMessage: salespathError,
        },
        "https://app.ignitewash.com"
      );
    }
    this.cancelSlowMessage();
    this.hideSlowMessage();
    this.paymentUpdateOutlet.hideSpinner();
    this.paymentUpdateOutlet.enableSubmit();

    this.errorListeners.forEach((listener) => listener(target));
  }

  startSlowMessageCountdown() {
    if (!this.slowMessageTimeoutId) {
      this.slowMessageTimeoutId = setTimeout(() => {
        this.slowMessageTarget.hidden = false;
      }, 15000); // 15 seconds
    }
  }

  cancelSlowMessage() {
    if (this.slowMessageTimeoutId) {
      clearTimeout(this.slowMessageTimeoutId);
      this.slowMessageTimeoutId = null;
    }
  }

  hideSlowMessage() {
    this.slowMessageTarget.hidden = true;
  }

  setAnonymousId() {
    this.anonymousIdTarget.value = document.querySelector("body").dataset.anonymous_id;
  }

  updateTos() {
    const productId = this.getSelectedProductId();
    if (!productId || !this.hasTosContentTarget) {
      return;
    }

    if (this.tosContentPathValue) {
      Turbo.visit(`${this.tosContentPathValue}?product_id=${productId}`, { frame: "tos_content" });
    }
  }

  updateTotal() {
    let total = 0;

    if (this.hasQuantityTarget) {
      this.quantityTargets.forEach((quantityTarget) => {
        const quantity = parseInt(quantityTarget.value) || 0;
        const price = parseFloat(quantityTarget.dataset.price) || 0;
        total += (quantity * price) / 100;
      });
    } else {
      this.productTargets.forEach((productTarget) => {
        if (productTarget.checked) {
          const price = parseFloat(productTarget.dataset.price) || 0;
          total += price / 100;
        }
      });
    }

    this.totalTarget.textContent = total.toFixed(2);
  }

  checkQuantities() {
    if (this.quantityTargets.some((input) => parseInt(input.value ?? 0) > 0)) {
      this.quantityTargets[0].setCustomValidity("");
    } else {
      this.quantityTargets[0].setCustomValidity("At least one product is required.");
    }
  }

  getSelectedProductId() {
    for (const product of this.productTargets) {
      if (product.checked) {
        return product.value;
      }
    }
    return null;
  }

  // The Stimulus action behind every field that feeds the estimate. Only the fetch is debounced: a reply
  // already in flight has to stop counting the moment the shopper changes something, or it lands inside
  // the 1.5s window with a still-current id and repaints — including hiding the totals of a product that
  // isn't blocked at all.
  debouncedEstimateTax() {
    this.taxEstimateRequestId++;
    if (this.nexusQuoteUrlValue) this.syncNexusPrices();
    this.runDebouncedEstimateTax();
  }

  // Everything the prices already on hand can answer is answered now rather than after the
  // debounce. The form stays submittable throughout that 1.5s, so a shopper who switches plan or
  // location and submits immediately must not be looking at the previous one's total.
  syncNexusPrices() {
    if (this.nexusPrices && this.nexusQuoteKey() !== this.nexusPricedKey) {
      this.clearNexusPrices();
    } else if (this.nexusPrices) {
      this.paintNexusTotals();
    }
  }

  async estimateTax() {
    if (!this.taxForSalesEnabledValue) {
      return;
    }

    if (this.nexusQuoteUrlValue) {
      await this.quoteFromNexus();
      return;
    }

    const selectedProductId = this.getSelectedProductId();

    // Estimates are debounced per keystroke and can resolve out of order, so only the newest one is
    // allowed to touch the DOM. A blocked reply hides the totals, so a stale one could otherwise leave an
    // eligible product showing no price at all.
    const requestId = ++this.taxEstimateRequestId;

    if (this.shouldEstimateTax(selectedProductId)) {
      const { subtotal, tax, total, blocked } = await this.fetchTaxEstimate({
        customer_email: sanitizeCreditCard(this.emailTarget.value),
        customer_first_name: sanitizeCreditCard(this.firstNameTarget.value),
        customer_last_name: sanitizeCreditCard(this.lastNameTarget.value),
        customer_phone: this.phoneTarget.value,
        membership_license_plate: this.hasLicensePlateTarget
          ? sanitizeCreditCard(this.licensePlateTarget.value)
          : "",
        product_id: selectedProductId,
        wash_connect_site_id: this.hasLocationSelectTarget ? this.locationSelectTarget.value : "",
      });

      if (requestId !== this.taxEstimateRequestId) return;

      // A recent member the product is configured to block has no price to show. They aren't told here;
      // PurchaseOrder rejects the sale with the form's block-sale message when they submit.
      if (blocked) {
        this.taxEstimateTarget.hidden = true;
      } else {
        this.subtotalTarget.textContent = subtotal;
        this.totalWithTaxTarget.textContent = total;
        this.taxTarget.textContent = tax;

        this.taxEstimateTarget.hidden = false;
      }
    } else {
      this.taxEstimateTarget.hidden = true;
    }
  }

  async trackEmailEntered() {
    if (!this.emailTarget.value?.trim()) return;

    const payload = {
      checkout_form_id: this.checkoutFormIdTarget.value,
      email: sanitizeCreditCard(this.emailTarget.value),
      first_name: sanitizeCreditCard(this.firstNameTarget.value),
      last_name: sanitizeCreditCard(this.lastNameTarget.value),
      parent_url: this.parentUrlTarget.value,
      product_id: this.getSelectedProductId(),
    };

    createRinsedEvent({ name: "Email entered in checkout form", payload });
  }

  trackPrefilledEmail() {
    // Handle direct page load where email is server-rendered
    if (this.emailTarget.value?.trim()) {
      this.trackEmailEntered();
      return;
    }

    // Handle iframe load where values are set via iFrameResizer message
    window.addEventListener(
      "allValuesSetFromParent",
      () => {
        if (this.emailTarget.value?.trim()) {
          this.trackEmailEntered();
        }
      },
      { once: true }
    );
  }

  shouldEstimateTax(selectedProductId) {
    if (!selectedProductId || selectedProductId === "") {
      return false;
    }

    if (this.taxEstimateRequiresPiiValue) {
      return (
        this.emailTarget.value !== "" &&
        this.firstNameTarget.value !== "" &&
        this.lastNameTarget.value !== "" &&
        this.phoneTarget.value !== "" &&
        (!this.hasLocationSelectTarget || this.locationSelectTarget.value !== "")
      );
    }

    return true;
  }

  async fetchTaxEstimate(payload) {
    const response = await fetch("/taxes/estimate", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    return await response.json();
  }

  // Nexus owns pricing — a NexusProduct carries no price in the CRM — so the form asks Nexus what
  // its products cost at the selected location. One request prices every product, which serves both
  // the per-product labels and the totals block for whichever one is selected.
  // The location dropdown is replaced by a hidden nexus_location_id input when the shopper arrives
  // through the Checkout Locator or a ?site_id link, so neither flow has a locationSelect target.
  // Falls back to the named input, the same way nexus_stripe_controller reads it.
  selectedNexusLocationId() {
    if (this.hasLocationSelectTarget) return this.locationSelectTarget.value;

    return this.element.querySelector('[name="nexus_location_id"]')?.value || "";
  }

  // Everything a quote's prices depend on. The applied code joins the location here so that applying
  // one invalidates the map; the NUL keeps a code that contains the location id from colliding.
  nexusQuoteKey() {
    return `${this.selectedNexusLocationId()}\u0000${this.appliedDiscountCode}`;
  }

  async quoteFromNexus() {
    const locationId = this.selectedNexusLocationId();
    if (!locationId) {
      this.clearNexusPrices();
      return;
    }

    const key = this.nexusQuoteKey();
    // A quote depends only on the location and the applied code, but this runs off every field that
    // feeds the tax estimate, so re-fetching per keystroke would fan out a Nexus call per product for
    // nothing. Selecting a product just repaints from the prices already on hand.
    if (key !== this.nexusQuotedKey && key !== this.nexusInFlightKey) {
      // Bumped per quote request, not per estimate: two requests can be outstanding for the same
      // key (switch away and back while the first is slow), and the key alone cannot tell them
      // apart, so an older reply could clear the newer one's marker or overwrite its prices.
      const token = ++this.nexusQuoteToken;
      this.nexusInFlightKey = key;
      // The totals block renders unhidden, so without this a slow first quote leaves the shopper
      // reading "SUBTOTAL: $" with no number after it.
      if (!this.nexusPrices) this.taxEstimateTarget.hidden = true;
      const requestedCode = this.appliedDiscountCode;
      const reply = await this.fetchNexusQuote(locationId, requestedCode);
      let products = reply?.products || null;
      // The uncoded quote is the plan's regular price. The wallet sheet needs it once a code
      // applies, to present "discounted first charge, then regular" rather than claiming the
      // discount recurs forever. Stashed before the staleness checks: a shopper who applies a
      // code while this quote is in flight supersedes it, but the reply is still a valid regular
      // price for its location — discarding it would leave the sheet on Amount Pending until the
      // code is removed. Cached per location, because a token ordering only means something
      // within one location's replies: two can be outstanding for one location (the A → B → A
      // switch), where the newest reply's values win and an older reply only fills products the
      // newer one failed to price — while a reply for another location must neither clobber this
      // one nor be judged by its token.
      // Known limit, deliberately unsupported for now: an automatic intro promo
      // (applies_for_n_billings on an auto rule) folds into this uncoded total, so the "regular"
      // amount can itself be a temporary price. The quote response carries no discount durations
      // today; the whole form shares that gap, not just the wallet sheet.
      if (products && !requestedCode) {
        this.nexusBaseByLocation ||= {};
        const cached = this.nexusBaseByLocation[locationId];
        if (!cached || token > cached.token) {
          this.nexusBaseByLocation[locationId] = {
            token,
            products: cached ? { ...cached.products, ...products } : products,
          };
        } else {
          cached.products = { ...products, ...cached.products };
        }
        // A coded quote may already be painted and waiting on this baseline; the stripe
        // controller dedups, so a redundant dispatch on the live path is harmless.
        if (locationId === this.selectedNexusLocationId()) this.dispatchWalletDisplay();
      }
      if (token !== this.nexusQuoteToken) return;

      this.nexusInFlightKey = null;

      // Staleness is judged by the quote key rather than the shared estimate counter: that counter
      // is bumped by every name/email/phone keystroke, which would throw away a reply the shopper is
      // still waiting on and start the whole fan-out again.
      if (key !== this.nexusQuoteKey()) return;

      if (!products) {
        this.clearNexusPrices();
        return;
      }

      // Merged, not replaced: across the two attempts a different product can be the one Nexus
      // failed to price, and replacing would drop a price the earlier reply had recovered — then
      // mark the still-incomplete key as done. Scoped to the same key so neither another location's
      // prices nor another code's discounts can leak into this map.
      products = this.nexusPricedKey === key ? { ...this.nexusPrices, ...products } : products;
      this.nexusPrices = products;
      this.nexusPricedKey = key;
      // The server drops a code it will not spend a Nexus call on (too short, or the form has the
      // field turned off) and answers with ordinary uncoded prices. Those carry no per-code status,
      // which would otherwise read as "nothing wrong" and let the code through to the order intent.
      this.nexusAcceptedCode = reply.discount_code || "";
      // A per-product failure returns a partial map with a 200. Leaving the key unrecorded lets the
      // next estimate try again, but only once: a product Nexus will never price (no price at this
      // location) would otherwise re-fan-out on every keystroke forever.
      if (this.hasEveryNexusPrice(products) || this.nexusRetriedIncomplete) {
        this.nexusQuotedKey = key;
      } else {
        this.nexusRetriedIncomplete = true;
        // Queue the retry rather than waiting for the shopper to touch something: someone who
        // loads the form, reads the prices and never types would otherwise sit with a plan that
        // shows none. The flag above caps this at one extra attempt.
        this.runDebouncedEstimateTax();
      }
      this.paintNexusPrices(products);
    }

    this.paintNexusTotals();
  }

  hasEveryNexusPrice(products) {
    return this.productPriceTargets.every((priceTarget) => products[priceTarget.dataset.productId]);
  }

  // subtotal, not a separate list price: each product is quoted alone at quantity one, so the
  // quote's subtotal is that product's price net of any automatic discount — the number the
  // shopper is actually charged, and the one the totals below agree with.
  paintNexusPrices(products) {
    this.productPriceTargets.forEach((priceTarget) => {
      const price = products[priceTarget.dataset.productId];
      priceTarget.textContent = price ? `$${price.subtotal}` : "";
      priceTarget.hidden = !price;
    });

    // data-price is what updateTotal() and the tracking payloads read, so keep it in step.
    this.productTargets.forEach((productTarget) => {
      const price = products[productTarget.value];
      if (price) productTarget.dataset.price = Math.round(parseFloat(price.subtotal) * 100);
    });
  }

  paintNexusTotals() {
    const selected = this.nexusPrices?.[this.getSelectedProductId()];
    if (selected) {
      // Gross when a code discounted this product, so SUBTOTAL - DISCOUNT + TAX = TOTAL reads right;
      // net otherwise, which keeps automatic discounts folded into the subtotal as they always were.
      this.subtotalTarget.textContent = selected.subtotal_before_discount ?? selected.subtotal;
      this.taxTarget.textContent = selected.tax;
      this.totalWithTaxTarget.textContent = selected.total;
      if (this.hasDiscountRowTarget) {
        this.discountTarget.textContent = selected.discount ?? "0.00";
        this.discountRowTarget.hidden = selected.discount_code_status !== "applied";
      }
    }
    this.taxEstimateTarget.hidden = !selected;
    this.paintDiscountCodeStatus(selected);
    this.dispatchWalletDisplay();
  }

  // Tells the nexus-stripe controller what the Apple Pay sheet should display for the current
  // selection. Everything in the payload is Nexus-sourced (quote total, sellable label and billing
  // cadence); a null detail reverts the sheet to its default Amount Pending display.
  dispatchWalletDisplay() {
    window.dispatchEvent(new CustomEvent("nexusWalletDisplay", { detail: this.walletDisplayDetail() }));
  }

  // Nexus forms are always single-select (quantity inputs only render on all-Sitewatch-Ewash
  // forms), so the payload is the checked product's quote entry.
  walletDisplayDetail() {
    if (!this.nexusPrices) return null;

    const entry = this.nexusPrices[this.getSelectedProductId()];
    if (!entry?.billing || !entry.label) return null;

    if (entry.billing === "one_time") {
      if (!entry.total_cents) return null;
      return { kind: "one_time", amountCents: entry.total_cents, label: entry.label };
    }

    // A code that discounted this product changes what the sheet must claim: the coded total is
    // only the first charge, and the ongoing amount is the uncoded quote's total. That split is
    // safe for every shopper-enterable code: Nexus rejects applies_for_n_billings (the
    // multi-billing intro marker) on code and voucher rules — it is auto-discount-only — so an
    // entered code can never touch a recharge. Without the base quote (location switched after
    // the code was applied) fall back to Amount Pending — a missing number beats presenting the
    // discount as the forever price. A first charge of 0 is legitimate here (100%-off code), so
    // only the regular amount must be positive.
    if (entry.discount_code_status === "applied") {
      const base =
        this.nexusBaseByLocation?.[this.selectedNexusLocationId()]?.products?.[this.getSelectedProductId()];
      if (!base?.total_cents) return null;
      return {
        kind: "recurring",
        amountCents: base.total_cents,
        trialAmountCents: entry.total_cents,
        label: entry.label,
        billingPeriod: entry.billing,
      };
    }

    if (!entry.total_cents) return null;
    return {
      kind: "recurring",
      amountCents: entry.total_cents,
      label: entry.label,
      billingPeriod: entry.billing,
    };
  }

  // Per selected product, because a code can apply to one plan and miss another. Repaints from the
  // cached map on every product change, so switching plans explains itself without a round trip.
  paintDiscountCodeStatus(selected) {
    if (!this.hasDiscountCodeStatusTarget) return;

    const status = this.discountCodeVerdict(selected);
    this.discountCodeStatusTarget.textContent = status?.message || "";
    this.discountCodeStatusTarget.hidden = !status?.message;
    this.discountCodeStatusTarget.dataset.status = status?.status || "";
  }

  // What the applied code did to the selected product, or null when there is nothing to say yet.
  // A code the server refused to look up gets the generic rejection rather than silence.
  discountCodeVerdict(selected) {
    if (!this.appliedDiscountCode) return null;
    // A quote still in flight, or one that failed outright, has not judged anything. Claiming a
    // rejection there would flash a false error and block a checkout the server is willing to take.
    if (this.nexusAcceptedCode === null) return null;
    if (this.nexusAcceptedCode !== this.appliedDiscountCode) {
      return { status: "not_applied", message: this.discountCodeTarget?.dataset.rejectedMessage };
    }
    if (!selected?.discount_code_status) return null;

    return { status: selected.discount_code_status, message: selected.discount_code_message };
  }

  async fetchNexusQuote(locationId, discountCode) {
    try {
      const response = await post(this.nexusQuoteUrlValue, {
        body: JSON.stringify({
          nexus_location_id: locationId,
          discount_code: discountCode || undefined,
          loyalty_program_redemption_id: this.loyaltyProgramRedemptionId(),
        }),
        contentType: "application/json",
        responseKind: "json",
      });
      if (!response.ok) return null;

      const payload = await response.json;
      return payload?.products ? payload : null;
    } catch (error) {
      // A pricing outage must not take the form down with it: the shopper can still buy, and Nexus
      // prices the order authoritatively at checkout either way.
      this.log("CheckoutFormsController.fetchNexusQuote failed", error);
      return null;
    }
  }

  clearNexusPrices() {
    this.nexusQuotedKey = null;
    this.nexusPricedKey = null;
    this.nexusRetriedIncomplete = false;
    this.nexusPrices = null;
    this.nexusAcceptedCode = null;
    this.paintDiscountCodeStatus(null);
    if (this.hasDiscountRowTarget) this.discountRowTarget.hidden = true;
    this.productPriceTargets.forEach((priceTarget) => {
      priceTarget.textContent = "";
      priceTarget.hidden = true;
    });
    this.taxEstimateTarget.hidden = true;
    this.dispatchWalletDisplay();
  }

  loyaltyProgramRedemptionId() {
    return this.element.querySelector('[name="loyalty_program_redemption_id"]')?.value || undefined;
  }

  // Applying is a deliberate click rather than a blur: blur does not reliably fire before a direct
  // click on Submit, and re-quoting per keystroke would blank the prices the shopper is reading.
  applyDiscountCode(event) {
    event?.preventDefault();
    if (!this.hasDiscountCodeTarget) return;

    const code = sanitizeCreditCard(this.discountCodeTarget.value.trim());
    this.discountCodeTarget.value = code;
    // Unchanged text still re-quotes unless the code is already applied. A rejection can be
    // temporary — reserved_elsewhere tells the shopper to wait and try again — and a no-op here
    // would leave them stuck behind a stale verdict with no way back but editing or reloading.
    if (code === this.appliedDiscountCode && this.discountCodeApplied()) return;

    this.appliedDiscountCode = code;
    this.nexusQuotedKey = null;
    this.debouncedEstimateTax();
  }

  // The typed value has diverged from the prices on screen, so the old verdict no longer describes
  // what is in the box. The applied code itself stays put until Apply replaces it.
  discountCodeChanged() {
    if (!this.hasDiscountCodeStatusTarget) return;
    if (this.discountCodeTarget.value.trim() === this.appliedDiscountCode) return;

    this.discountCodeStatusTarget.hidden = true;
  }

  // The last gate before payment. Fails open when there is nothing to judge — an unquoted code or a
  // failed quote is the server's problem, and Nexus::Checkout refuses it there rather than charging
  // full price.
  validateDiscountCode() {
    if (!this.hasDiscountCodeTarget) return true;

    const typed = this.discountCodeTarget.value.trim();
    // Emptying the field does not un-quote anything: the discounted totals stay on screen, so
    // letting a blank through would charge the undiscounted intent total against the price the
    // shopper is still reading. Applying an empty code re-quotes and clears them properly.
    if (!typed && !this.appliedDiscountCode) return true;

    if (typed !== this.appliedDiscountCode) {
      this.showDiscountCodeError(this.discountCodeTarget.dataset.reapplyPrompt);
      return false;
    }

    const verdict = this.discountCodeVerdict(this.nexusPrices?.[this.getSelectedProductId()]);
    if (!verdict || verdict.status === "applied") return true;

    this.showDiscountCodeError(verdict.message);
    return false;
  }

  discountCodeApplied() {
    return this.discountCodeVerdict(this.nexusPrices?.[this.getSelectedProductId()])?.status === "applied";
  }

  showDiscountCodeError(message) {
    if (this.hasDiscountCodeStatusTarget) {
      this.discountCodeStatusTarget.textContent = message || "";
      this.discountCodeStatusTarget.hidden = !message;
      this.discountCodeStatusTarget.dataset.status = "rejected";
    }
    this.discountCodeTarget.focus();
  }

  handleMessage(event) {
    if (
      event.data?.action &&
      ["focusFirstName", "disableSubmit", "enableSubmit"].includes(event.data.action)
    ) {
      this.log("CheckoutFormsController.handleMessage", event);

      if (event.origin !== "https://app.ignitewash.com") {
        this.log("handleMessage: ignoring message from unknown origin");
        return;
      }

      if (event.data.action === "focusFirstName") {
        this.firstNameTarget.focus();
      } else if (event.data.action === "disableSubmit") {
        this.paymentUpdateOutlet.disableSubmit();
      } else if (event.data.action === "enableSubmit") {
        this.paymentUpdateOutlet.enableSubmit();
      }
    }
  }

  setupPhoneOrEmailValidation() {
    this.paymentUpdateOutlet.addAsyncSubmitInterrupt(async () => this.validatePhoneOrEmail());
  }

  // Returns true when the phone-or-email requirement is satisfied (or not
  // configured). When neither field is filled, shows the inline error and
  // returns false. Called both from the final-submit interrupt and, for
  // multi-step checkouts, before advancing past step 1 (where the fields live)
  // so the error doesn't surface next to hidden fields on the payment step.
  validatePhoneOrEmail() {
    if (!this.phoneOrEmailRequiredValue) return true;

    const email = this.emailTarget.value?.trim();
    const phone = this.phoneTarget.value?.trim();

    if (!email && !phone) {
      this.showPhoneOrEmailError();
      return false;
    }

    this.clearPhoneOrEmailError();
    return true;
  }

  showPhoneOrEmailError() {
    if (this.phoneOrEmailErrorElement) return;

    this.phoneOrEmailErrorElement = document.createElement("div");
    this.phoneOrEmailErrorElement.className = "text-red-500 text-sm mt-1";
    this.phoneOrEmailErrorElement.textContent =
      "Please provide either an email address or phone number";
    this.emailTarget.closest(".input").before(this.phoneOrEmailErrorElement);
    this.emailTarget.focus();
  }

  clearPhoneOrEmailError() {
    if (this.phoneOrEmailErrorElement) {
      this.phoneOrEmailErrorElement.remove();
      this.phoneOrEmailErrorElement = null;
    }
  }

  log(message, details) {
    try {
      logger.log(message, details);
    } catch (error) {
      try {
        logger.log("error logging");
      } catch (error2) {}
    }
  }
}
